import React, { useState } from 'react';
import { Search, Filter, Plus, Eye, Pencil, Phone, Mail, Calendar, Star, Target, TrendingUp, User, MessageSquare, MoreVertical, RefreshCw, Trash2, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddLeadModal from '../Modals/AddLeadModal';
import LeadDetailModal from '../Modals/LeadDetailModal';
import EditLeadModal from '../Modals/EditLeadModal';
import ImportLeadsModal from '../Modals/ImportLeadsModal';
import { useLeads } from '../../hooks/useSupabase';
import { useAuth, mockUsers } from '../../contexts/AuthContext';
import { deleteLeadDirect } from '../../hooks/useSupabase';
import { supabase } from '../../lib/supabase';

interface SeriousCandidatesProps {
}

const SeriousCandidates: React.FC<SeriousCandidatesProps> = () => {
  const { leads, loading, fetchLeads, addLead, updateLead } = useLeads();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [userBranchId, setUserBranchId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [consultantFilter, setConsultantFilter] = useState<string>('all');
  const itemsPerPage = 20;

  // Fetch user's branch and profiles
  React.useEffect(() => {
    const fetchUserBranchAndProfiles = async () => {
      if (!user?.id) return;

      try {
        // Kullanıcının branch_id'sini profiles tablosundan çek
        const { data: profile } = await supabase
          .from('profiles')
          .select('branch_id')
          .eq('id', user.id)
          .maybeSingle();

        const branchId = profile?.branch_id || null;
        setUserBranchId(branchId);

        // Aynı şubedeki tüm kullanıcıları çek
        if (branchId) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, name, role, unit_type, branch_id')
            .eq('branch_id', branchId)
            .eq('is_active', true);

          setProfiles(profilesData || []);
        }
      } catch (err) {
        console.error('Unexpected error fetching user branch:', err);
      }
    };

    fetchUserBranchAndProfiles();
  }, [user?.id]);

  // Fetch leads when component mounts
  React.useEffect(() => {
    fetchLeads();
  }, []);

  // Listen for new leads to update activities
  React.useEffect(() => {
    const handleNewLead = () => {
      // Refresh leads data
      fetchLeads();
      
      // Add new activity
      const newActivity = {
        id: Date.now(),
        type: 'lead_created',
        message: 'Yeni lead eklendi',
        time: 'Az önce',
        icon: Target,
        color: 'text-blue-600'
      };
      
      setRecentActivities(prev => [newActivity, ...prev.slice(0, 4)]);
    };

    const handleLeadStatusUpdate = (event: any) => {
      const { leadId, newStatus, source } = event.detail;
      
      if (source === 'proposal') {
        // Lead listesini yenile
        fetchLeads();
        
        // Aktivite ekle
        const lead = leads.find(l => l.id === leadId);
        if (lead) {
          const statusText = newStatus === 'accepted' ? 'Kabul Edildi' :
                           newStatus === 'rejected' ? 'Reddedildi' :
                           newStatus === 'interested' ? 'İlgili' :
                           newStatus === 'contacted' ? 'İletişim Kuruldu' : newStatus;
          
          const newActivity = {
            id: Date.now(),
            type: 'lead_status_updated',
            message: `${lead.first_name} ${lead.last_name} durumu: ${statusText} (Teklif üzerinden)`,
            time: 'Az önce',
            icon: Target,
            color: newStatus === 'accepted' ? 'text-green-600' : 
                   newStatus === 'rejected' ? 'text-red-600' : 'text-blue-600'
          };
          
          setRecentActivities(prev => [newActivity, ...prev.slice(0, 4)]);
        }
      }
    };

    window.addEventListener('newLeadAdded', handleNewLead);
    window.addEventListener('leadStatusUpdated', handleLeadStatusUpdate);
    return () => {
      window.removeEventListener('newLeadAdded', handleNewLead);
      window.removeEventListener('leadStatusUpdated', handleLeadStatusUpdate);
    };
  }, [fetchLeads, leads]);

  // Ensure leads is always an array
  const safeLeads = leads || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new_lead': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      case 'contacted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'qualified': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'unqualified': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'converted': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new_lead': return 'Yeni Lead';
      case 'called': return 'Arandı';
      case 'no_answer': return 'Cevapsız';
      case 'busy': return 'Meşgul';
      case 'meeting_scheduled': return 'Görüşme Sağlandı';
      case 'proposal_sent': return 'Teklif Gönderildi';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getContactRoleColor = (contactRole: string, creatorName?: string) => {
    if (contactRole === 'student') {
      if (creatorName === 'Betul Özbey' || creatorName === 'Kaya Bayezit') {
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-800';
      }
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800';
    }
    if (contactRole === 'guardian') {
      if (creatorName === 'Edanur Aydın') {
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800';
      }
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800';
    }
    return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const getServiceColor = (creatorName?: string) => {
    if (creatorName === 'Betul Özbey' || creatorName === 'Kaya Bayezit') {
      return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-800';
    }
    if (creatorName === 'Edanur Aydın') {
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800';
    }
    if (creatorName === 'Medine Güzel') {
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 border border-green-200 dark:border-green-800';
    }
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800';
  };

  const getSourceText = (source: string) => {
    switch (source) {
      case 'website': return 'Website';
      case 'referral': return 'Referans';
      case 'social-media': return 'Sosyal Medya';
      case 'advertisement': return 'Reklam';
      case 'walk-in': return 'Ziyaret';
      case 'other': return 'Diğer';
      default: return source;
    }
  };

  // Danışmanlar listesini oluştur (ciddi adaylar üzerinden)
  const consultants = React.useMemo(() => {
    const consultantMap = new Map<string, { id: string; name: string }>();

    safeLeads.filter(lead => lead.is_serious_candidate).forEach(lead => {
      if (lead.created_by) {
        const creatorName = profiles.find(p => p.id === lead.created_by)?.name;

        if (creatorName && !consultantMap.has(lead.created_by)) {
          consultantMap.set(lead.created_by, { id: lead.created_by, name: creatorName });
        }
      }
    });

    return Array.from(consultantMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [safeLeads, profiles]);

  // Arama yapılırken tüm lead'lerde ara - sadece ciddi adayları göster
  const filteredLeads = React.useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    const hasSearch = searchTerm.length > 0;

    return safeLeads.filter(lead => {
      // Sadece ciddi adayları göster
      if (!lead.is_serious_candidate) return false;

      const matchesSearch = !hasSearch ||
        lead.first_name?.toLowerCase().includes(searchLower) ||
        lead.last_name?.toLowerCase().includes(searchLower) ||
        lead.email?.toLowerCase().includes(searchLower) ||
        lead.phone?.includes(searchTerm) ||
        lead.emails?.some((email: string) => email?.toLowerCase().includes(searchLower)) ||
        lead.student_info?.first_name?.toLowerCase().includes(searchLower) ||
        lead.student_info?.last_name?.toLowerCase().includes(searchLower) ||
        lead.student_info?.phone?.includes(searchTerm) ||
        lead.student_info?.emails?.some((email: string) => email?.toLowerCase().includes(searchLower));

      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;

      const matchesConsultant = consultantFilter === 'all' ||
        (consultantFilter === 'mine' ? lead.created_by === user?.id : lead.created_by === consultantFilter);

      return matchesSearch && matchesStatus && matchesConsultant;
    });
  }, [safeLeads, searchTerm, statusFilter, consultantFilter, user?.id]);

  // Stats hesapla (sadece ciddi adaylar üzerinden)
  const stats = {
    totalLeads: filteredLeads.length,
    highPriorityLeads: filteredLeads.filter(l => l.priority === 'high').length,
    interestedLeads: filteredLeads.filter(l => l.status === 'interested').length,
  };

  // Sayfalama için lead'leri böl
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

  // Sayfa değiştiğinde en üste scroll
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Filtre değiştiğinde ilk sayfaya dön
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, consultantFilter]);

  const handleLeadAction = async (action: string, leadId: string) => {
    const lead = safeLeads.find(l => l.id === leadId);
    if (!lead) return;

    setSelectedLead(lead);

    switch (action) {
      case 'view':
        setShowDetailModal(true);
        break;
      case 'edit':
        setShowEditModal(true);
        break;
      case 'call':
        // Karel sistemi ile arama yap
        if (window.makeKarelCall && lead.phone) {
          const success = window.makeKarelCall(lead.phone);
          if (success) {
            console.log(`${lead.first_name} ${lead.last_name} aranıyor: ${lead.phone}`);
            
            // Arama aktivitesi kaydet
            const activity = {
              id: Date.now(),
              type: 'call_initiated',
              message: `${lead.first_name} ${lead.last_name} arandı (${lead.phone})`,
              time: 'Az önce',
              icon: Phone,
              color: 'text-green-600'
            };
            
            window.dispatchEvent(new CustomEvent('newActivity', { detail: activity }));
          } else {
            // Fallback: WhatsApp'a yönlendir
            const cleanPhone = lead.phone.replace(/\D/g, '');
            const message = encodeURIComponent(`Merhaba ${lead.first_name}, Pisa Educazione'den arıyorum. Size nasıl yardımcı olabilirim?`);
            window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
          }
        } else {
          alert('Telefon numarası bulunamadı.');
        }
        break;
      case 'email':
        if (lead.email) {
          const subject = encodeURIComponent('Pisa Educazione - Eğitim Danışmanlığı');
          const body = encodeURIComponent(`Merhaba ${lead.first_name},\n\nPisa Educazione'den size ulaşıyorum. İtalya'da eğitim fırsatları hakkında konuşmak isterim.\n\nSaygılarımla,\n${user?.name || 'Danışman'}`);
          window.open(`mailto:${lead.email}?subject=${subject}&body=${body}`, '_blank');
        }
        break;
      case 'delete':
        setShowDeleteConfirm(true);
        break;
      case 'qualify':
        try {
          await updateLead(leadId, {
            status: 'qualified',
            updated_at: new Date().toISOString()
          }, user?.id);
          fetchLeads(); // Refresh data
          alert('Lead başarıyla "Nitelikli" durumuna güncellendi!');
        } catch (error) {
          console.error('Lead güncelleme hatası:', error);
          alert('Lead güncellenirken hata oluştu.');
        }
        break;
      default:
        console.log(`Action: ${action} for lead: ${leadId}`);
    }
  };

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      const { error } = await updateLead(leadId, {
        status: newStatus,
        updated_at: new Date().toISOString()
      }, user?.id);

      if (error) {
        console.error('Lead durum güncelleme hatası:', error);
        alert('Lead durumu güncellenirken hata oluştu.');
        return;
      }

      alert(`Lead durumu "${getStatusText(newStatus)}" olarak güncellendi!`);
      fetchLeads(); // Refresh data
    } catch (error) {
      console.error('Lead durum güncelleme hatası:', error);
      alert('Lead durumu güncellenirken beklenmeyen bir hata oluştu.');
    }
  };

  const handleDeleteLead = async () => {
    if (!selectedLead) return;
    
    try {
      // deleteLead fonksiyonunu useLeads hook'undan al
      const { error } = await deleteLeadDirect(selectedLead.id);
      
      if (error) {
        console.error('Lead silme hatası:', error);
        alert('Lead silinirken hata oluştu.');
        return;
      }
      
      alert(`${selectedLead.first_name} ${selectedLead.last_name} başarıyla silindi!`);
      setShowDeleteConfirm(false);
      setSelectedLead(null);
      fetchLeads(); // Listeyi yenile
    } catch (error) {
      console.error('Lead silme hatası:', error);
      alert('Lead silinirken beklenmeyen bir hata oluştu.');
    }
  };
  const handleSaveLead = async (leadData: any): Promise<{ ok: boolean; error?: string }> => {
    try {
      const { data, error } = await addLead(leadData);

      if (error) {
        console.error('Lead ekleme hatası:', error);
        return { ok: false, error: error.message || 'Bilinmeyen hata' };
      }

      // Refresh the students list
      await fetchLeads();
      return { ok: true };
    } catch (error: any) {
      console.error('Lead ekleme hatası:', error);
      // Lead başarıyla oluşturulmuşsa hata gösterme
      if (error?.code !== 'PGRST116') {
        return { ok: false, error: error?.message || 'Beklenmeyen bir hata oluştu' };
      }
      return { ok: true };
    }
  };

  const getLeadScore = (lead: any) => {
    return lead.lead_score || 0;
  };

  const getLeadScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const handleEmailClick = (email: string) => {
    if (!email) return;

    // Gmail'de yeni mail oluştur
    const subject = encodeURIComponent('Pisa Educazione - Eğitim Danışmanlığı');
    const body = encodeURIComponent(`Merhaba,\n\nPisa Educazione'den size ulaşıyorum. İtalya'da eğitim fırsatları hakkında konuşmak isterim.\n\nSaygılarımla,\n${user?.name || 'Danışman'}`);

    // Gmail compose URL'si
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;

    // Yeni sekmede aç
    window.open(gmailUrl, '_blank');
  };

  const handleImportLeads = async (importedLeads: any[]): Promise<{ ok: boolean; error?: string; successCount?: number; errorCount?: number }> => {
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    for (const lead of importedLeads) {
      try {
        const { assigned_to, branch_id, ...leadData } = lead;

        const leadWithDefaults = {
          ...leadData,
          created_by: user?.id || null,
          status: lead.status || 'new_lead',
          priority: lead.priority || 'medium',
          source: lead.source || 'import',
          interested_countries: lead.interested_countries || ['İtalya'],
          interested_programs: lead.interested_programs || [],
          contact_role: lead.contact_role || 'student',
        };

        const { error } = await addLead(leadWithDefaults);
        if (error) {
          errorCount++;
          errors.push(`${lead.first_name} ${lead.last_name}: ${error.message}`);
          console.error('Error adding lead:', error);
        } else {
          successCount++;
        }
      } catch (error: any) {
        errorCount++;
        errors.push(`${lead.first_name} ${lead.last_name}: ${error.message}`);
        console.error('Exception adding lead:', error);
      }
    }

    await fetchLeads();

    if (errorCount > 0) {
      console.error('Import errors:', errors);
    }

    return {
      ok: successCount > 0,
      successCount,
      errorCount,
      error: errorCount > 0 ? `${errorCount} lead eklenemedi` : undefined,
    };
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Ciddi Adaylar</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Toplam {filteredLeads.length} ciddi aday • Sayfa {currentPage} / {totalPages} • Veritabanından canlı veri
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchLeads}
            disabled={loading}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Yenile</span>
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Upload className="w-4 h-4" />
            <span>İçe Aktar</span>
          </button>
          <button
            onClick={() => setShowAddLeadModal(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Yeni Lead</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Target className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalLeads}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Ciddi Aday Sayısı</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Star className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.highPriorityLeads}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Yüksek Öncelik</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.interestedLeads}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">İlgili Lead</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="İsim, telefon, email veya öğrenci bilgisi ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="lead">Yeni Lead</option>
            <option value="rejected">Red</option>
            <option value="enrolled">Kayıt</option>
          </select>

          <select
            value={consultantFilter}
            onChange={(e) => setConsultantFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Danışmanlar</option>
            <option value="mine">Sadece Benim</option>
            {consultants.map(consultant => (
              <option key={consultant.id} value={consultant.id}>
                {consultant.name}
              </option>
            ))}
          </select>

        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Lead'ler yükleniyor...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                <tr>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Lead</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İletişim</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Oluşturan</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Görüşme Türü</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İlgilendiği Hizmet</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İlgilendiği Program</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Eğitim Düzeyi</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İngilizce Seviyesi</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Follow-up</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Oluşturulma</th>
                  <th className="text-right py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedLeads.map((lead) => {
                  const leadScore = getLeadScore(lead);
                  
                  return (
                    <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">
                              {(lead.first_name?.[0] || '') + (lead.last_name?.[0] || '')}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {lead.first_name} {lead.last_name}
                            </p>
                            <div className="flex items-center space-x-2">
                              {lead.contact_role === 'guardian' && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getContactRoleColor('guardian', lead.creator?.name)}`}>
                                  Veli - {lead.guardian_info?.relationship === 'anne' ? 'Anne' :
                                          lead.guardian_info?.relationship === 'baba' ? 'Baba' :
                                          lead.guardian_info?.relationship === 'kardes' ? 'Kardeş' :
                                          lead.guardian_info?.relationship === 'diger' ? 'Diğer' :
                                          'Veli'}
                                </span>
                              )}
                              {lead.contact_role === 'student' && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getContactRoleColor('student', lead.creator?.name)}`}>
                                  Öğrenci
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          {lead.emails && lead.emails.length > 0 && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <Mail className="w-4 h-4" />
                              <button
                                onClick={() => handleEmailClick(lead.emails[0])}
                                className="truncate max-w-48 hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors cursor-pointer"
                                title="Gmail'de yeni mail oluştur"
                              >
                                {lead.emails[0]}
                              </button>
                              {lead.emails.length > 1 && (
                                <span className="text-xs text-gray-500">+{lead.emails.length - 1}</span>
                              )}
                            </div>
                          )}
                          {lead.phone && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <Phone className="w-4 h-4" />
                              <span>{lead.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center">
                          {(() => {
                            const creatorName = lead.created_by ?
                              (lead.creator?.name ||
                               profiles.find(p => p.id === lead.created_by)?.name ||
                               mockUsers.find(u => u.id === lead.created_by)?.name ||
                               'Bilinmiyor') :
                              'Sistemden Yüklendi';

                            const getCreatorColor = (name: string) => {
                              if (name === 'Sistemden Yüklendi') return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-800' };
                              if (name === 'Bilinmiyor') return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-700' };

                              if (name === 'Betul Özbey' || name === 'Kaya Bayezit') {
                                return { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-200 dark:border-cyan-800' };
                              }
                              if (name === 'Edanur Aydın') {
                                return { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800' };
                              }
                              if (name === 'Medine Güzel') {
                                return { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-200 dark:border-green-800' };
                              }

                              return { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800' };
                            };

                            const colors = getCreatorColor(creatorName);

                            return (
                              <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
                                {creatorName}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {lead.meeting_type === 'in-person' ? 'Fiziki Görüşme' :
                           lead.meeting_type === 'phone' ? 'Telefonda Görüşme' :
                           lead.meeting_type === 'video' ? 'Online Görüşme' :
                           lead.meeting_type === 'whatsapp' ? 'WhatsApp Mesajlaşma' :
                           lead.meeting_type === 'email' ? 'E-mail' : '-'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {lead.interested_services && Array.isArray(lead.interested_services) && lead.interested_services.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {lead.interested_services.slice(0, 2).map((service: string, idx: number) => (
                                <span key={idx} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getServiceColor(lead.creator?.name)}`}>
                                  {service === 'language_courses' ? 'Dil' :
                                   service === 'university_application' ? 'Ünv.' :
                                   service === 'visa_support' ? 'Vize' :
                                   service === 'accommodation' ? 'Kon.' :
                                   service === 'health_insurance' ? 'Sig.' :
                                   service === 'exam_prep' ? 'Sınav' : service}
                                </span>
                              ))}
                              {lead.interested_services.length > 2 && (
                                <span className="text-xs text-gray-500">+{lead.interested_services.length - 2}</span>
                              )}
                            </div>
                          ) : '-'}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {lead.interested_programs && Array.isArray(lead.interested_programs) && lead.interested_programs.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {lead.interested_programs.slice(0, 1).map((prog: string, idx: number) => (
                                <span key={idx} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getServiceColor(lead.creator?.name)}`}>
                                  {prog}
                                </span>
                              ))}
                              {lead.interested_programs.length > 1 && (
                                <span className="text-xs text-gray-500">+{lead.interested_programs.length - 1}</span>
                              )}
                            </div>
                          ) : '-'}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {(() => {
                            const eduLevel = lead.contact_role === 'guardian'
                              ? lead.student_info?.education_level
                              : lead.education_level;
                            if (!eduLevel) return '-';
                            if (eduLevel === 'Yüksek Lisans') return 'Y. Lisans';
                            return eduLevel;
                          })()}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center">
                          {(() => {
                            const engLevel = lead.contact_role === 'guardian'
                              ? lead.student_info?.english_level
                              : lead.english_level;

                            if (!engLevel || engLevel.trim() === '') {
                              return <span className="text-sm text-gray-400 dark:text-gray-500">-</span>;
                            }

                            const levelUpper = engLevel.toUpperCase();
                            let badgeColor = 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';

                            if (levelUpper.includes('C2') || levelUpper.includes('C1')) {
                              badgeColor = 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
                            } else if (levelUpper.includes('B2')) {
                              badgeColor = 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
                            } else if (levelUpper.includes('B1')) {
                              badgeColor = 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800';
                            } else if (levelUpper.includes('A2') || levelUpper.includes('A1')) {
                              badgeColor = 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800';
                            }

                            return (
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${badgeColor}`}>
                                {engLevel}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center">
                          {lead.follow_up_required ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800">
                              Evet
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                              Hayır
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {lead.meeting_date ? (
                            <>
                              {format(new Date(lead.meeting_date), 'dd MMM yyyy', { locale: tr })}
                              {lead.meeting_time && (
                                <span className="block text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                  {lead.meeting_time}
                                </span>
                              )}
                            </>
                          ) : (
                            format(new Date(lead.created_at), 'dd MMM yyyy', { locale: tr })
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end space-x-2">
                          <button 
                            onClick={() => handleLeadAction('view', lead.id)}
                            className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title="Detayları Görüntüle"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleLeadAction('edit', lead.id)}
                            className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                            title="Düzenle"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {lead.status === 'new_lead' && (
                            <button 
                              onClick={() => handleLeadAction('qualify', lead.id)}
                              className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                              title="Nitelikli Durumuna Çevir"
                            >
                              <TrendingUp className="w-4 h-4" />
                            </button>
                          )}
                          <div className="relative">
                            <button 
                              onClick={() => handleLeadAction('delete', lead.id)}
                              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                              title="Lead'i Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && filteredLeads.length > 0 && totalPages > 1 && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Gösterilen: {startIndex + 1}-{Math.min(endIndex, filteredLeads.length)} / {filteredLeads.length} lead
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="flex items-center space-x-1">
                {/* İlk sayfa */}
                {currentPage > 3 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      1
                    </button>
                    {currentPage > 4 && (
                      <span className="text-gray-400 px-2">...</span>
                    )}
                  </>
                )}

                {/* Önceki sayfa */}
                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    {currentPage - 1}
                  </button>
                )}

                {/* Mevcut sayfa */}
                <button
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg font-medium"
                >
                  {currentPage}
                </button>

                {/* Sonraki sayfa */}
                {currentPage < totalPages && (
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    {currentPage + 1}
                  </button>
                )}

                {/* Son sayfa */}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && (
                      <span className="text-gray-400 px-2">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="text-sm text-gray-600 dark:text-gray-400">
              Sayfa {currentPage} / {totalPages}
            </div>
          </div>
        </div>
      )}

      {!loading && filteredLeads.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-gray-500 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {searchTerm ? 'Lead bulunamadı' : 'Henüz lead yok'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchTerm 
              ? 'Arama kriterlerinizi değiştirmeyi deneyin' 
              : 'İlk lead\'inizi ekleyerek başlayın'
            }
          </p>
          {!searchTerm && (
            <button 
              onClick={() => setShowAddLeadModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              İlk Lead'i Ekle
            </button>
          )}
        </div>
      )}
      
      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onSave={handleSaveLead}
      />

      {/* Import Leads Modal */}
      <ImportLeadsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportLeads}
      />

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
          onEdit={() => {
            setShowDetailModal(false);
            setShowEditModal(true);
          }}
          onUpdateLead={(id, updates) => updateLead(id, updates, user?.id)}
        />
      )}
      
      {/* Edit Lead Modal */}
      {selectedLead && (
        <EditLeadModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
          onSave={async (updatedData) => {
            try {
              const { data, error } = await updateLead(selectedLead.id, updatedData, user?.id);

              if (error) {
                console.error('Lead güncelleme hatası:', error);
                return { ok: false, error: 'Lead güncellenirken hata oluştu.' };
              }

              alert(`${selectedLead.first_name} ${selectedLead.last_name} başarıyla güncellendi!`);
              fetchLeads(); // Listeyi yenile
              return { ok: true, data };
            } catch (error) {
              console.error('Lead güncelleme hatası:', error);
              return { ok: false, error: 'Beklenmeyen bir hata oluştu.' };
            }
          }}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Lead'i Sil</h3>
                  <p className="text-gray-600 dark:text-gray-400">Bu işlem geri alınamaz</p>
                </div>
              </div>
              
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                <strong>{selectedLead.first_name} {selectedLead.last_name}</strong> adlı lead'i kalıcı olarak silmek istediğinize emin misiniz? 
                Bu işlem geri alınamaz ve tüm veriler kaybolacaktır.
              </p>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSelectedLead(null);
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleDeleteLead}
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Sil</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeriousCandidates;
